import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { SilentRequest } from "@azure/msal-browser";

import { BehaviorSubject, Observable } from "rxjs";

import { PowerBIQueryResponse, PowerBIQueryRow, PowerBIReport } from "@core/models";

@Injectable({ providedIn: 'root' })
export class PowerBiService {

    private _onAccessTokenChanged: BehaviorSubject<string>;

    /**
     * Constructor
     * @param _http 
     * @param _msalService 
     * @param msalGuardConfig 
     */
    constructor(private _http: HttpClient, 
            private _msalService: MsalService,
            @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration){
        this._onAccessTokenChanged = new BehaviorSubject(null);
    }

    get onAccessTokenChanged(): Observable<any> {
        return this._onAccessTokenChanged.asObservable();
    }

    get accessToken(): string{
        return this._onAccessTokenChanged.value;
    }

    /**
     * Registrar el token para power bi
     */
    register(): void {
        // Obtiene el access token para power bi
        this.getAccessToken()
        .then(resp => this._onAccessTokenChanged.next(resp))
        .catch(error => this._onAccessTokenChanged.error(error));
    }

    /**
     * Obtiene la información del reporte para embeber
     * @param {string} groupId Indica el id del grupo donde se encuentra el reporte
     * @param {string} reportId Indica el id del reporte
     * @returns {Promise<PowerBIReport>} Devuelve la información del reporte de power bi
     */
    async getReportEmbed(groupId: string, reportId: string): Promise<PowerBIReport>{

        // Crea el endpoint para obtener la información del reporte
        const endpoint = `https://api.powerbi.com/v1.0/myorg/groups/${groupId}/reports/${reportId}`

        // Crea el encapezado de la petición
        const headers = {
            'Authorization' : `Bearer ${this.accessToken}`,
            'Content-type': 'application/json'
        };

        // Realiza la llamada al api
        return this._http.get<PowerBIReport>(endpoint, { headers: headers}).toPromise();
    }

    /**
     * Ejecuta una query DAX contra un dataset (modelo semántico) de Power BI.
     *
     * Usa el endpoint `executeQueries` del API REST, que devuelve las filas ya
     * evaluadas: sirve para traer números al front sin embeber un reporte.
     *
     * El acceso lo resuelve el token del usuario, no una cuenta de servicio: si
     * no tiene permiso sobre el dataset el API responde 401/403 y la promesa
     * rechaza. Quien llama decide qué hacer con eso (lo normal es no mostrar el
     * bloque, no romper la pantalla).
     *
     * Limitaciones del endpoint: una sola query por llamada, sólo `EVALUATE`
     * (nada que escriba en el modelo) y hasta 100.000 filas.
     *
     * @param {string} datasetId Id del dataset a consultar
     * @param {string} query Query DAX, tiene que terminar en un EVALUATE
     * @param {string} groupId Workspace del dataset; 'me' o vacío para el propio
     * @returns {Promise<PowerBIQueryRow[]>} Filas de la primera tabla del resultado
     */
    async executeQuery(datasetId: string, query: string, groupId?: string): Promise<PowerBIQueryRow[]> {

        // El token puede no estar todavía: se pide en el momento, así el
        // servicio sirve aunque nadie haya llamado antes a register()
        const accessToken = this.accessToken || await this.getAccessToken();

        // Sin workspace (o con 'me') el dataset se busca en el del propio usuario
        const scope = groupId && groupId !== 'me' ? `groups/${groupId}/` : '';
        const endpoint = `https://api.powerbi.com/v1.0/myorg/${scope}datasets/${datasetId}/executeQueries`;

        const headers = {
            'Authorization': `Bearer ${accessToken}`,
            'Content-type': 'application/json'
        };

        const body = {
            queries: [{ query }],
            // Sin esto las celdas vacías no viajan y las filas quedan desparejas
            serializerSettings: { includeNulls: true }
        };

        const resp = await this._http.post<PowerBIQueryResponse>(endpoint, body, { headers }).toPromise();

        return resp?.results?.[0]?.tables?.[0]?.rows || [];
    }

    /**
     * Obtiene el access token para power bi
     * @returns {Promise<string>} Devuelve el access token de power bi
     */
    private async getAccessToken(): Promise<string> {
        const siletRequest = {...this.msalGuardConfig.authRequest} as SilentRequest;
        siletRequest.scopes = ['https://analysis.windows.net/powerbi/api/.default'];

        const result = await this._msalService.acquireTokenSilent(siletRequest).toPromise();

        return result.accessToken;
    }

}
